import { Fragment, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { PageNavbar } from '@/pages/account';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { UserRoles } from '@/constant';
import { useCurrentUserRoles } from '@/utils/userRole';
import { ConfirmationDialog } from '@/components/dialogBox';
import { BackButton } from '@/components/button';
import {
  useBulkUpdateHarborsMutation,
  useExportHarborsCsvQuery,
} from '@/services/HarborManagementService';
import type {
  BulkUpdateHarborsResult,
  BulkUpdateValidationError,
} from '@/types/harbor-management.types';

function downloadBlob(content: string, mimeType: string, fileName: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function HarborBulkUpdate() {
  const { enqueueSnackbar } = useSnackbar();
  const { currentLayout } = useLayout();
  const roles = useCurrentUserRoles();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPlatformAdmin = roles.includes(UserRoles.ADMIN);

  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    BulkUpdateValidationError[]
  >([]);
  const [validatedResult, setValidatedResult] =
    useState<BulkUpdateHarborsResult | null>(null);
  const [applyResult, setApplyResult] =
    useState<BulkUpdateHarborsResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    'validate' | 'apply' | null
  >(null);
  const [shouldExport, setShouldExport] = useState(false);

  const [bulkUpdate] = useBulkUpdateHarborsMutation();
  const { data: exportResponse, isError: isExportError } =
    useExportHarborsCsvQuery({}, { skip: !shouldExport });

  useEffect(() => {
    if (!shouldExport) return;

    if (isExportError) {
      enqueueSnackbar('Failed to download current data. Please try again.', {
        variant: 'error',
      });
      setShouldExport(false);
      return;
    }

    if (exportResponse) {
      if (exportResponse.fileUrl) {
        const link = document.createElement('a');
        link.href = exportResponse.fileUrl;
        link.download = 'harbours.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        enqueueSnackbar('No harbour data available to export.', {
          variant: 'info',
        });
      }
      setShouldExport(false);
    }
  }, [exportResponse, isExportError, shouldExport, enqueueSnackbar]);

  if (!isPlatformAdmin) {
    return <Navigate to="/error/404" replace />;
  }

  const resetResults = () => {
    setValidationErrors([]);
    setValidatedResult(null);
    setApplyResult(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    resetResults();
  };

  const handleApiError = (err: unknown, failureMessage: string) => {
    const apiErr = err as { data?: { errors?: BulkUpdateValidationError[] } };
    setValidatedResult(null);
    setValidationErrors(apiErr?.data?.errors ?? []);
    enqueueSnackbar(failureMessage, { variant: 'error' });
  };

  const handleValidate = async () => {
    if (!file) return;
    setPendingAction('validate');
    try {
      const result = await bulkUpdate({ file, dryRun: true }).unwrap();
      setValidationErrors([]);
      setValidatedResult(result);
      setApplyResult(null);
      enqueueSnackbar(
        result.updated.length > 0
          ? `Validation passed — ${result.updated.length} harbour(s) will be updated`
          : 'All rows passed validation.',
        { variant: 'success' }
      );
    } catch (err) {
      handleApiError(err, 'Fix validation errors before applying.');
    } finally {
      setPendingAction(null);
    }
  };

  const handleApplyConfirmed = async () => {
    if (!file) return;
    setShowConfirm(false);
    setPendingAction('apply');
    try {
      const result = await bulkUpdate({ file, dryRun: false }).unwrap();
      setValidationErrors([]);
      setValidatedResult(null);
      setApplyResult(result);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      enqueueSnackbar(
        `Successfully updated ${result.updated.length} harbour(s).`,
        { variant: 'success' }
      );
    } catch (err) {
      handleApiError(err, 'Update failed. Check errors below.');
    } finally {
      setPendingAction(null);
    }
  };

  const canApply = !!file && !!validatedResult && validationErrors.length === 0;
  const isValidating = pendingAction === 'validate';
  const isApplying = pendingAction === 'apply';

  const renderResultTable = (result: BulkUpdateHarborsResult) => (
    <div className="space-y-3">
      {result.ignoredColumns.length > 0 && (
        <p className="text-2sm text-gray-500 mb-0">
          {`Ignored columns (not updated): ${result.ignoredColumns.join(', ')}`}
        </p>
      )}
      {result.unchanged.length > 0 && (
        <p className="text-2sm text-gray-500 mb-0">
          {`${result.unchanged.length} harbour(s) matched but had no changes and will be skipped.`}
        </p>
      )}
      {result.updated.length === 0 ? (
        <p className="text-gray-500 text-sm mb-0">No changes.</p>
      ) : (
        <div className="overflow-x-auto overflow-y-auto max-h-96 border rounded-lg">
          <table className="table table-sm table-bordered align-middle text-sm mb-0">
            <thead>
              <tr>
                <th className="min-w-[220px]">Harbor Name</th>
                <th className="min-w-[280px]">Harbor ID</th>
                <th className="min-w-[400px]">Changed fields</th>
              </tr>
            </thead>
            <tbody>
              {result.updated.map((row) => (
                <tr key={row.harborId}>
                  <td>{row.name}</td>
                  <td className="font-mono text-xs">{row.harborId}</td>
                  <td>
                    <ul className="mb-0 ps-4 list-disc">
                      {row.changedFields.map((change) => (
                        <li key={change.field}>
                          <strong>{change.field}:</strong> {change.oldValue}
                          {' => '}
                          {change.newValue}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <Fragment>
      <PageNavbar />
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <div className="flex items-center gap-3">
                <BackButton customBackPath="/habor-management" />
                <div>
                  <ToolbarPageTitle text="Harbour bulk update" />
                  <ToolbarDescription>
                    Download the current harbour data, edit it, then upload the
                    edited CSV to correct existing harbour records in bulk.
                  </ToolbarDescription>
                </div>
              </div>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div className="card mb-5">
          <div className="card-body space-y-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Step 1: Export current data
                </label>
                <p className="text-2sm text-gray-500 mb-0">
                  Download a CSV export of the harbours you want to correct.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => setShouldExport(true)}
                disabled={shouldExport}
              >
                {shouldExport ? (
                  <span className="animate-spin me-1">
                    <KeenIcon type="filled" icon="spinner" aria-hidden="true" />
                  </span>
                ) : (
                  <KeenIcon type="filled" icon="file-down" className="me-1" />
                )}
                {shouldExport ? 'Downloading…' : 'Download current data'}
              </button>
            </div>

            <div className="border-t pt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-1"
                  htmlFor="bulk-update-file"
                >
                  Step 2: Upload edited CSV
                </label>
                <p className="text-2sm text-gray-500 mb-0">
                  Upload the edited CSV export. Only changed fields need
                  editing.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="btn btn-sm btn-light cursor-pointer mb-0">
                  Upload CSV file
                  <input
                    id="bulk-update-file"
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onClick={(e) => {
                      // Clear the value on every click so re-selecting the
                      // same file path still fires onChange (browsers skip
                      // the change event when the path hasn't changed).
                      e.currentTarget.value = '';
                    }}
                    onChange={handleFileChange}
                  />
                </label>
                <span className="text-sm text-gray-600">
                  {file ? file.name : 'No file selected'}
                </span>
              </div>
            </div>

            <div className="border-t pt-5 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={handleValidate}
                disabled={!file || isValidating || isApplying}
              >
                {isValidating && (
                  <span className="animate-spin me-1">
                    <KeenIcon type="filled" icon="spinner" aria-hidden="true" />
                  </span>
                )}
                {isValidating ? 'Validating…' : 'Validate'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => setShowConfirm(true)}
                disabled={!canApply || isApplying}
              >
                {isApplying && (
                  <span className="animate-spin me-1">
                    <KeenIcon type="filled" icon="spinner" aria-hidden="true" />
                  </span>
                )}
                {isApplying ? 'Applying…' : 'Apply update'}
              </button>
            </div>
          </div>
        </div>

        {validationErrors.length > 0 && (
          <div className="card mb-5">
            <div className="card-header">
              <h3 className="card-title mb-0 text-danger">Validation errors</h3>
            </div>
            <div className="card-body overflow-x-auto">
              <div className="overflow-y-auto max-h-96 border rounded-lg">
                <table className="table table-sm table-bordered align-middle text-sm mb-0">
                  <thead>
                    <tr>
                      <th className="w-[80px]">Row</th>
                      <th className="min-w-[220px]">Harbor Name</th>
                      <th className="min-w-[280px]">Harbor ID</th>
                      <th className="min-w-[180px]">Field</th>
                      <th className="min-w-[320px]">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationErrors.map((error, index) => (
                      <tr key={`${error.row}-${error.field}-${index}`}>
                        <td>{error.row}</td>
                        <td>{error.name ?? '—'}</td>
                        <td className="font-mono text-xs">
                          {error.harborId ?? '—'}
                        </td>
                        <td>{error.field}</td>
                        <td>{error.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {validatedResult && (
          <div className="card mb-5">
            <div className="card-header flex justify-between items-center">
              <h3 className="card-title mb-0">
                <KeenIcon
                  type="filled"
                  icon="check-circle"
                  className="text-success me-2"
                />
                {`Validation passed — ${validatedResult.updated.length} harbour(s) will be updated`}
              </h3>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() =>
                  downloadBlob(
                    JSON.stringify(validatedResult, null, 2),
                    'application/json',
                    'harbour-bulk-update-preview.json'
                  )
                }
              >
                Download report
              </button>
            </div>
            <div className="card-body">
              {renderResultTable(validatedResult)}
            </div>
          </div>
        )}

        {applyResult && (
          <div className="card mb-5">
            <div className="card-header flex justify-between items-center">
              <h3 className="card-title mb-0">
                <KeenIcon
                  type="filled"
                  icon="check-circle"
                  className="text-success me-2"
                />
                {`Update complete — ${applyResult.updated.length} harbour(s) updated`}
              </h3>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() =>
                  downloadBlob(
                    JSON.stringify(applyResult, null, 2),
                    'application/json',
                    'harbour-bulk-update-report.json'
                  )
                }
              >
                Download report
              </button>
            </div>
            <div className="card-body">{renderResultTable(applyResult)}</div>
          </div>
        )}
      </Container>

      <ConfirmationDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleApplyConfirmed}
        message={`This will update ${validatedResult?.updated.length ?? 0} harbour(s). This cannot be undone. Continue?`}
        confirmButtonText="Apply update"
        confirmButtonVariant="warning"
        isLoading={isApplying}
      />
    </Fragment>
  );
}
